import React, { useMemo } from "react";
import { Input, Select, DatePicker } from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { fuzzyIncludes } from "../../../utils/fuzzyMatch";
import type { AseoFilterValues } from "./AseoFilterBar";

type Option = { value: string; label: string };

// Nombres de mes en español SIN depender del locale global de dayjs (que
// algunas páginas setean y otras no). Así el atajo "Mes" siempre rotula igual.
const MESES_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const ESTADO_OPTIONS: Option[] = [
  { value: "pendiente", label: "Pendiente" },
  { value: "en_proceso", label: "En proceso" },
  { value: "completado", label: "Completado" },
  { value: "pagado", label: "Pagado" },
  { value: "cancelado", label: "Cancelado" },
];

interface QuickAseoFiltersProps {
  filterValues: AseoFilterValues;
  setFilterValues: React.Dispatch<React.SetStateAction<AseoFilterValues>>;
  allAseadores: Option[];
  allPropiedades: Option[];
  sectorSelectOptions: Option[];
  filterSortAseadores: (a: any, b: any) => number;
  filterSortPropiedad: (a: any, b: any) => number;
  // El filtro "Aseador" solo tiene sentido para admin: al aseador el backend
  // no le manda la lista de aseadoras, así que el dropdown quedaría vacío.
  showAseadorFilter?: boolean;
}

/**
 * Barra de filtros RÁPIDOS, siempre visible, con los filtros más usados de la
 * agenda de aseos (búsqueda en notas, mes, aseador, propiedad, sector, estado).
 * Comparte el MISMO estado `filterValues` que el panel "Filtros avanzados"
 * (AseoFilterBar): no duplica lógica ni filtrado, solo "sube a la superficie"
 * los controles más pedidos para que no haya que abrir el acordeón. Round
 * 2026-07-14 — pedido de UX del owner ("faltan filtros fáciles y accesibles").
 */
const QuickAseoFilters: React.FC<QuickAseoFiltersProps> = ({
  filterValues,
  setFilterValues,
  allAseadores,
  allPropiedades,
  sectorSelectOptions,
  filterSortAseadores,
  filterSortPropiedad,
  showAseadorFilter = true,
}) => {
  // El atajo "Mes" y el rango exacto del panel avanzado escriben el MISMO
  // campo (`fechaInicio`). Derivamos el mes del rango SOLO si cubre exactamente
  // un mes calendario completo; si no, el selector de mes queda vacío (pero el
  // rango sigue vigente y visible en "Filtros avanzados" + en los chips).
  const monthValue = useMemo<Dayjs | null>(() => {
    const [a, b] = filterValues.fechaInicio;
    if (!a || !b) return null;
    const start = dayjs(a);
    const end = dayjs(b);
    if (!start.isValid() || !end.isValid()) return null;
    const isFullMonth =
      start.isSame(start.startOf("month"), "day") &&
      end.isSame(start.endOf("month"), "day");
    return isFullMonth ? start : null;
  }, [filterValues.fechaInicio]);

  const handleMonthChange = (m: Dayjs | null) => {
    setFilterValues((f) => ({
      ...f,
      fechaInicio: m
        ? [m.startOf("month"), m.endOf("month")]
        : [null, null],
    }));
  };

  return (
    <div className="aseos-quick-filters">
      <span className="qf-legend">
        <FilterOutlined />
        Filtros
      </span>

      {/* Buscar en notas o por código (clave interna `observaciones` por
          compatibilidad; "#1472"/"142" filtra por SUBSTRING tanto el código
          propio del aseo como la estadía vinculada — ver filtrarAseos en
          aseos.tsx). */}
      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
        placeholder="Buscar en notas, #aseo o #estadía…"
        className="qf-notas"
        value={filterValues.observaciones}
        onChange={(e) =>
          setFilterValues((f) => ({ ...f, observaciones: e.target.value }))
        }
      />

      {/* Mes */}
      <DatePicker
        picker="month"
        allowClear
        placeholder="Mes"
        className="qf-mes"
        value={monthValue}
        onChange={handleMonthChange}
        format={(v) => `${MESES_ES[v.month()]} ${v.year()}`}
      />

      {/* Aseador (solo admin) */}
      {showAseadorFilter && (
        <Select
          mode="multiple"
          showSearch
          allowClear
          maxTagCount="responsive"
          placeholder="Aseador"
          className="qf-select"
          options={allAseadores}
          value={filterValues.aseadores}
          onChange={(val) =>
            setFilterValues((f) => ({ ...f, aseadores: val }))
          }
          filterOption={(input, option) => fuzzyIncludes(option?.label, input)}
          filterSort={filterSortAseadores}
        />
      )}

      {/* Propiedad */}
      <Select
        mode="multiple"
        showSearch
        allowClear
        maxTagCount="responsive"
        placeholder="Propiedad"
        className="qf-select qf-select--wide"
        options={allPropiedades}
        value={filterValues.propiedad}
        onChange={(val) => setFilterValues((f) => ({ ...f, propiedad: val }))}
        filterOption={(input, option) => fuzzyIncludes(option?.label, input)}
        filterSort={filterSortPropiedad}
      />

      {/* Sector */}
      <Select
        mode="multiple"
        showSearch
        allowClear
        maxTagCount="responsive"
        placeholder="Sector"
        className="qf-select"
        options={sectorSelectOptions}
        value={filterValues.sector}
        onChange={(val) => setFilterValues((f) => ({ ...f, sector: val }))}
        filterOption={(input, option) => fuzzyIncludes(option?.label, input)}
      />

      {/* Estado */}
      <Select
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        placeholder="Estado"
        className="qf-select"
        options={ESTADO_OPTIONS}
        value={filterValues.estado}
        onChange={(val) => setFilterValues((f) => ({ ...f, estado: val }))}
      />
    </div>
  );
};

export default QuickAseoFilters;
